import React, { ReactElement, SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NumberInput from 'commons/src/components/input/Number';
import CustomSlider from 'commons/src/components/slider/CustomSlider';
import { colors, sensorUnits, ThresholdSlidercolorMap } from 'commons/src/constants';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { ThresholdRange } from 'commons/src/models/commonTypeScript';
import { CustomOptions, ThresholdOption } from 'commons/src/models/customThresholdModels';
import ThresholdDot, { DotGradientVariant } from './ThresholdDot';
import styles from './ThresholdSlider.module.scss';
import useThresholdSlider from './useThresholdSlider';

const TRANSITION_LABELS_2 = ['CustomThresholds.GoodToFair', 'CustomThresholds.FairToPoor'] as const;
const TRANSITION_LABELS_4 = [
    'CustomThresholds.PoorToFair',
    'CustomThresholds.FairToGood',
    'CustomThresholds.GoodToFair',
    'CustomThresholds.FairToPoor',
] as const;

const DOT_GRADIENT_2: DotGradientVariant[] = ['greenToYellow', 'yellowToRed'];
const DOT_GRADIENT_4: DotGradientVariant[] = ['redToYellow', 'yellowToGreen', 'greenToYellow', 'yellowToRed'];
/** Temperature: low = blue, high = red */
const DOT_GRADIENT_TEMP: DotGradientVariant[] = ['blue', 'red'];
const TEMP_GRADIENT_COLORS = [colors.graphBlue, colors.graphGreen, colors.graphRed] as const;

export type Props = {
    sensorType: SensorTypes;
    ranges: ThresholdRange[];
    thresholdOptions: ThresholdOption[];
    customOptions?: CustomOptions;
    selectedValues: number[];
    setSelectedValues: (values: number[]) => void;
    staticValues?: boolean;
    /** Optional unit for range bar min/max labels (e.g. "Bq/m³") */
    unit?: string;
};

const ThresholdSlider = ({
    sensorType,
    selectedValues,
    setSelectedValues,
    thresholdOptions,
    customOptions,
    staticValues,
    ranges,
    unit: unitKey,
}: Props): ReactElement => {
    const { t: txt } = useTranslation();
    const { updateInputValue, thumbColors, normalizeRanges, sliderConfig, isValidNumberInput } = useThresholdSlider({
        selectedValues,
        setSelectedValues,
        thresholdOptions,
        customOptions,
        sensorType,
    });

    const unitStr = unitKey ? sensorUnits[unitKey] ?? unitKey : '';
    const transitionLabels = thresholdOptions.length === 4 ? TRANSITION_LABELS_4 : TRANSITION_LABELS_2;
    const formatWithUnit = (value: number): string => (unitStr ? `${value} ${unitStr}` : String(value));
    const getTransitionLabel = (index: number): string => {
        if (sensorType === SensorTypes.temp && thresholdOptions[index])
            return txt(`ThresholdTypeTemp.${thresholdOptions[index].type}`);
        return transitionLabels[index]
            ? txt(transitionLabels[index])
            : txt(`ThresholdType.${thresholdOptions[index]?.type}`);
    };

    const fullWidthGradient = useMemo(() => {
        const { min, max } = sliderConfig;
        const nr = normalizeRanges(ranges, min, max);
        const extendedRanges = nr.map((range, index) => {
            if (index === 0) return { ...range, to: selectedValues[0] };
            if (index === 1) return { ...range, from: selectedValues[0], to: selectedValues[1] };
            if (index === 2) return { ...range, from: selectedValues[1], to: selectedValues[2] };
            if (index === 3) return { ...range, from: selectedValues[2], to: selectedValues[3] };
            if (index === 4) return { ...range, from: selectedValues[3] };
            return range;
        });
        const stops: string[] = [];
        let currentStart = min;
        extendedRanges.forEach((range, index) => {
            const start = range.from !== undefined ? range.from : currentStart;
            const end = range.to !== undefined ? range.to : max;
            const startPercent = ((start - min) / (max - min)) * 100;
            const endPercent = ((end - min) / (max - min)) * 100;
            const color =
                sensorType === SensorTypes.temp
                    ? TEMP_GRADIENT_COLORS[Math.min(index, TEMP_GRADIENT_COLORS.length - 1)]
                    : ThresholdSlidercolorMap[range.rating];
            stops.push(`${color} ${startPercent}% ${endPercent}%`);
            currentStart = end;
        });
        return `linear-gradient(to right, ${stops.join(', ')})`;
    }, [ranges, sliderConfig, selectedValues, normalizeRanges, sensorType]);

    const gradientSegments = useMemo(() => {
        const { min, max } = sliderConfig;
        const nr = normalizeRanges(ranges, min, max);
        const rangeSpan = max - min || 1;
        return nr.map((range, index) => {
            const from = index === 0 ? min : selectedValues[index - 1];
            const to = index === nr.length - 1 ? max : selectedValues[index];
            const fromNum = typeof from === 'number' && Number.isFinite(from) ? from : min;
            const toNum = typeof to === 'number' && Number.isFinite(to) ? to : max;
            const startPercent = ((fromNum - min) / rangeSpan) * 100;
            const endPercent = ((toNum - min) / rangeSpan) * 100;
            let ratingLabel: string;
            if (range.rating === 'GOOD') {
                ratingLabel = txt('ThresholdKeys.NormalLevel');
            } else if (range.rating === 'FAIR') {
                ratingLabel = txt('ThresholdKeys.WarningLevel');
            } else {
                ratingLabel = txt('ThresholdKeys.ActionLevel');
            }
            return {
                startPercent,
                endPercent,
                rating: range.rating,
                from: fromNum,
                to: toNum,
                ratingLabel,
            };
        });
    }, [ranges, sliderConfig, selectedValues, normalizeRanges, txt]);

    const boundaryDots = useMemo(() => {
        const { min, max } = sliderConfig;
        const fmt = (v: number): string => (unitStr ? `${v} ${unitStr}` : String(v));
        return selectedValues.map((value, index) => {
            const labelKey = thresholdOptions.length === 4 ? TRANSITION_LABELS_4[index] : TRANSITION_LABELS_2[index];
            const label = labelKey ? txt(labelKey) : '';
            return {
                leftPercent: ((value - min) / (max - min)) * 100,
                value,
                tooltip: label ? `${label}: ${fmt(value)}` : fmt(value),
            };
        });
    }, [sliderConfig, selectedValues, thresholdOptions.length, unitStr, txt]);

    return (
        <div className={styles.thresholdSetting}>
            <div className={styles.fullWidthGradientWrap}>
                <span className={styles.rangeLabel}>{formatWithUnit(sliderConfig.min)}</span>
                <div className={styles.fullWidthGradientLineWrap}>
                    <div
                        className={styles.fullWidthGradientLine}
                        style={{ background: fullWidthGradient }}
                        aria-hidden
                    />
                    {gradientSegments.map((seg, segIndex) => (
                        <div
                            // eslint-disable-next-line react/no-array-index-key -- segments can share startPercent/endPercent, index ensures unique key
                            key={`seg-${segIndex}-${seg.startPercent}-${seg.endPercent}-${seg.rating}`}
                            className={styles.gradientSegmentHover}
                            style={{
                                left: `${seg.startPercent}%`,
                                width: `${seg.endPercent - seg.startPercent}%`,
                            }}
                            title={`${seg.ratingLabel}: ${formatWithUnit(seg.from)} - ${formatWithUnit(seg.to)}`}
                            role="img"
                            aria-label={`${seg.ratingLabel}: ${formatWithUnit(seg.from)} - ${formatWithUnit(seg.to)}`}
                        />
                    ))}
                    {boundaryDots.map(dot => (
                        <span
                            key={`dot-${dot.value}-${dot.leftPercent}`}
                            className={styles.gradientBoundaryDot}
                            style={{ left: `${dot.leftPercent}%` }}
                            title={dot.tooltip}
                            aria-label={dot.tooltip}
                        />
                    ))}
                </div>
                <span className={styles.rangeLabel}>{formatWithUnit(sliderConfig.max)}</span>
            </div>
            {!staticValues && (
                <div className={styles.rangeBar}>
                    <span className={styles.rangeLabel}>{formatWithUnit(sliderConfig.min)}</span>
                    <div className={styles.sliderWithLines}>
                        <div className={styles.slider}>
                            <CustomSlider
                                value={selectedValues}
                                onChange={(event: Event, newValue: number[]): void => {
                                    setSelectedValues(newValue);
                                }}
                                ranges={normalizeRanges(ranges, sliderConfig.min, sliderConfig.max)}
                                min={sliderConfig.min}
                                max={sliderConfig.max}
                                step={sliderConfig.step}
                                thumbColors={thumbColors}
                                showGradient
                                valueLabelDisplay="on"
                                sensorType={sensorType}
                                aria-label={txt('CustomThresholds.Slider')}
                            />
                        </div>
                        <div className={styles.coloredLines} aria-hidden>
                            {/* eslint-disable react/no-array-index-key -- ordered slider thumbs, values can duplicate */}
                            {selectedValues.map((value, i) => (
                                <span
                                    key={`line-${i}-${value}`}
                                    className={styles.coloredLine}
                                    style={{
                                        left: `${
                                            ((value - sliderConfig.min) / (sliderConfig.max - sliderConfig.min)) * 100
                                        }%`,
                                        backgroundColor: thumbColors[i],
                                    }}
                                />
                            ))}
                            {/* eslint-enable react/no-array-index-key */}
                        </div>
                    </div>
                    <span className={styles.rangeLabel}>{formatWithUnit(sliderConfig.max)}</span>
                </div>
            )}
            <div className={styles.inputs}>
                {thresholdOptions.length === 4 ? (
                    <div className={styles.thresholdOptionsGrid}>
                        {/* First item (left-aligned) */}
                        <div className={styles.thresholdOptionsGridItem}>
                            <div className={styles.threshold}>
                                <ThresholdDot
                                    type={thresholdOptions[0].type}
                                    variant={sensorType === SensorTypes.temp ? DOT_GRADIENT_TEMP[0] : DOT_GRADIENT_4[0]}
                                />
                                <div className={styles.thresholdType}>{getTransitionLabel(0)}</div>
                            </div>
                            {staticValues ? (
                                <div className={styles.staticValue}>
                                    {thresholdOptions[0].value}
                                    {unitStr && ` ${unitStr}`}
                                </div>
                            ) : (
                                <div className={styles.marginTop}>
                                    <NumberInput
                                        id={thresholdOptions[0].type}
                                        validate={!isValidNumberInput(0)}
                                        currentValue={selectedValues[0]?.toString() || ''}
                                        size="small"
                                        minValue={sliderConfig.min}
                                        maxValue={sliderConfig.max}
                                        step={customOptions?.thresholdDelta || 1}
                                        onChange={(event: SyntheticEvent<HTMLInputElement>): void => {
                                            updateInputValue(event, 0);
                                        }}
                                    />
                                    {unitStr && <span className={styles.unitLabel}>{unitStr}</span>}
                                </div>
                            )}
                        </div>
                        {/* Middle items */}
                        <div className={styles.thresholdOptionsGridMiddle}>
                            {thresholdOptions.slice(1, -1).map((option, idx) => {
                                const index = idx + 1;
                                const dotVariant =
                                    sensorType === SensorTypes.temp ? DOT_GRADIENT_TEMP[index] : DOT_GRADIENT_4[index];
                                return (
                                    <div key={option.type}>
                                        <div className={styles.threshold}>
                                            <ThresholdDot type={option.type} variant={dotVariant} />
                                            <div className={styles.thresholdType}>{getTransitionLabel(index)}</div>
                                        </div>
                                        {staticValues ? (
                                            <div className={styles.staticValue}>
                                                {option.value}
                                                {unitStr && ` ${unitStr}`}
                                            </div>
                                        ) : (
                                            <div className={styles.marginTop}>
                                                <NumberInput
                                                    id={option.type}
                                                    validate={!isValidNumberInput(index)}
                                                    currentValue={selectedValues[index]?.toString() || ''}
                                                    size="small"
                                                    minValue={sliderConfig.min}
                                                    maxValue={sliderConfig.max}
                                                    step={customOptions?.thresholdDelta || 1}
                                                    onChange={(event: SyntheticEvent<HTMLInputElement>): void => {
                                                        updateInputValue(event, index);
                                                    }}
                                                />
                                                {unitStr && <span className={styles.unitLabel}>{unitStr}</span>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {/* Last item (right-aligned) */}
                        <div className={styles.thresholdOptionsGridItem}>
                            <div className={styles.threshold}>
                                <ThresholdDot
                                    type={thresholdOptions[3].type}
                                    variant={sensorType === SensorTypes.temp ? DOT_GRADIENT_TEMP[1] : DOT_GRADIENT_4[3]}
                                />
                                <div className={styles.thresholdType}>{getTransitionLabel(3)}</div>
                            </div>
                            {staticValues ? (
                                <div className={styles.staticValue}>
                                    {thresholdOptions[3].value}
                                    {unitStr && ` ${unitStr}`}
                                </div>
                            ) : (
                                <div className={styles.marginTop}>
                                    <NumberInput
                                        id={thresholdOptions[3].type}
                                        validate={!isValidNumberInput(3)}
                                        currentValue={selectedValues[3]?.toString() || ''}
                                        size="small"
                                        minValue={sliderConfig.min}
                                        maxValue={sliderConfig.max}
                                        step={customOptions?.thresholdDelta || 1}
                                        onChange={(event: SyntheticEvent<HTMLInputElement>): void => {
                                            updateInputValue(event, 3);
                                        }}
                                    />
                                    {unitStr && <span className={styles.unitLabel}>{unitStr}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={styles.thresholdOptions}>
                        {thresholdOptions?.map((option, index) => {
                            const gradientVariants = DOT_GRADIENT_2;
                            const dotVariant = gradientVariants[index];
                            return (
                                <div key={option.type}>
                                    <div className={styles.threshold}>
                                        <ThresholdDot type={option.type} variant={dotVariant} />
                                        <div className={styles.thresholdType}>{getTransitionLabel(index)}</div>
                                    </div>
                                    {staticValues ? (
                                        <div className={styles.staticValue}>
                                            {option.value}
                                            {unitStr && ` ${unitStr}`}
                                        </div>
                                    ) : (
                                        <div className={styles.marginTop}>
                                            <NumberInput
                                                id={option.type}
                                                validate={!isValidNumberInput(index)}
                                                currentValue={selectedValues[index]?.toString() || ''}
                                                size="small"
                                                minValue={sliderConfig.min}
                                                maxValue={sliderConfig.max}
                                                step={customOptions?.thresholdDelta || 1}
                                                onChange={(event: SyntheticEvent<HTMLInputElement>): void => {
                                                    updateInputValue(event, index);
                                                }}
                                            />
                                            {unitStr && <span className={styles.unitLabel}>{unitStr}</span>}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ThresholdSlider;
